import { vec2 } from "gl-matrix"
import { RadialMask } from "@/core/types"
import { degrees, radians } from "@/core/utils/math"
import {
	Layer,
	layerToBounds,
	scaleLayer,
} from "@/components/canvas-controls/canvas-controls-utils"
import { clamp, lerp } from "@/utils/math"

interface ScaleHandlerOptions {
	imageSize: vec2
	pointer: vec2
	pointerStart: vec2
	controlPoint: vec2
	keepAspect: boolean
	fromCenter: boolean
}

export function handleMaskScale(mask: RadialMask, options: ScaleHandlerOptions): RadialMask {
	// Scale layer coords by image size
	const layer = maskToLayer(mask, options.imageSize)

	// Scale pointer coords by image size
	const startPos = options.pointerStart
	const pos = options.pointer
	vec2.mul(startPos, startPos, options.imageSize)
	vec2.mul(pos, pos, options.imageSize)

	const controlPoint: vec2 = vec2.clone(options.controlPoint)
	const oppositePoint: vec2 = [1 - controlPoint[0], 1 - controlPoint[1]]
	const origin: vec2 = options.fromCenter ? [0.5, 0.5] : oppositePoint

	// Map origin point to bounds
	const bounds = layerToBounds(layer)
	origin[0] = lerp(bounds.left, bounds.right, origin[0])
	origin[1] = lerp(bounds.top, bounds.bottom, origin[1])
	controlPoint[0] = lerp(bounds.left, bounds.right, controlPoint[0])
	controlPoint[1] = lerp(bounds.top, bounds.bottom, controlPoint[1])

	vec2.rotate(pos, pos, [layer.x, layer.y], radians(mask.angle))
	vec2.rotate(startPos, startPos, [layer.x, layer.y], radians(mask.angle))

	const delta = vec2.sub(vec2.create(), pos, startPos)
	const point = vec2.add(vec2.create(), controlPoint, delta)

	const nx = point[0] - origin[0]
	const ny = point[1] - origin[1]

	let dx = controlPoint[0] - origin[0]
	let dy = controlPoint[1] - origin[1]

	// Get scale from origin point
	let sx = dx !== 0 ? nx / dx : 1
	let sy = dy !== 0 ? ny / dy : 1

	if (options.keepAspect) {
		dx = Math.abs(dx)
		dy = Math.abs(dy)
		sx = sy = (sx * dx + sy * dy) / (dx + dy)
	}

	scaleLayer(layer, [sx, sy], origin, mask.angle)

	return {
		...mask,
		...layerToMask(layer, options.imageSize),
	}
}

interface RotateHandlerOptions {
	imageSize: vec2
	pointer: vec2
	pointerStart: vec2
}

export function handleMaskRotate(mask: RadialMask, options: RotateHandlerOptions) {
	let angle = mask.angle

	// Scale layer coords by image size
	const layer = maskToLayer(mask, options.imageSize)

	// Scale pointer coords by image size
	const startPos = options.pointerStart
	const pos = options.pointer
	vec2.mul(startPos, startPos, options.imageSize)
	vec2.mul(pos, pos, options.imageSize)

	const origin: vec2 = [layer.x, layer.y]

	// Find angle
	const startAngle = getAngle(startPos, origin)
	const currAngle = getAngle(pos, origin)
	const angleDelta = startAngle - currAngle

	angle = (angle - degrees(angleDelta)) % 360

	return {
		...mask,
		angle,
	}
}

interface FeatherHandlerOptions {
	pointer: vec2
	pointerStart: vec2
}

export function handleMaskFeather(mask: RadialMask, options: FeatherHandlerOptions) {
	const startPos = options.pointerStart
	const pos = options.pointer

	const origin: vec2 = [mask.centerX, mask.centerY]

	vec2.lerp(origin, startPos, origin, 2)

	// Find distance to center
	const startDist = vec2.dist(startPos, origin)
	const dist = vec2.dist(pos, origin)
	const distanceDelta = (dist / startDist) * 2 - 1

	const feather = clamp(1 - (1 - mask.feather) * distanceDelta, 0, 1)

	return {
		...mask,
		feather,
	}
}

function maskToLayer(mask: RadialMask, imageSize: vec2): Layer {
	return {
		x: mask.centerX * imageSize[0],
		y: mask.centerY * imageSize[1],
		width: mask.radiusX * 2 * imageSize[0],
		height: mask.radiusY * 2 * imageSize[1],
	}
}

function layerToMask(
	layer: Layer,
	imageSize: vec2,
): Pick<RadialMask, "centerX" | "centerY" | "radiusX" | "radiusY"> {
	return {
		centerX: layer.x / imageSize[0],
		centerY: layer.y / imageSize[1],
		radiusX: Math.abs(layer.width / imageSize[0] / 2),
		radiusY: Math.abs(layer.height / imageSize[1] / 2),
	}
}

function getAngle(p1: vec2, p2: vec2) {
	return Math.atan2(p1[0] - p2[0], p1[1] - p2[1])
}
